import { useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, Landmark, Scale, ShieldCheck } from "lucide-react";
import { V } from "@/lib/vocab";
import { ViewOnlyPill, LockedTile, lockedProps, SettingsReadOnly } from "@/components/SettingsSection";

// Legal guardrails for how the AI answers residents: the liability disclaimer and the statutory
// fallback. These are SETTINGS (toggles + a label/URL), so they live in Settings → Policies —
// they used to sit on the AI Documents page, which is where knowledge lives, not configuration.
const DEFAULT_LIABILITY = "Heads up: I'm an automated assistant. My answers are general guidance based on this community's documents — they may not be complete or current, they aren't legal advice, and the community isn't liable for decisions made based on them. Please confirm anything important with your management team. By continuing, you acknowledge this.";

export function LegalCompliance({ tenantId, readOnly }) {
  const lockReason = useContext(SettingsReadOnly);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}`).then((r) => {
      const t = r.data;
      setCfg({
        liability: t.liability || { enabled: false, mode: "soft", text: "" },
        statutory_fallback: t.statutory_fallback || { enabled: false, label: "Texas Property Code", url: "https://statutes.capitol.texas.gov/Docs/PR/htm/PR.209.htm", mode: "refer" },
      });
    }).catch(() => {});
  }, [tenantId]);
  if (!cfg) return null;
  const upL = (k, v) => setCfg((c) => ({ ...c, liability: { ...c.liability, [k]: v } }));
  const upS = (k, v) => setCfg((c) => ({ ...c, statutory_fallback: { ...c.statutory_fallback, [k]: v } }));
  const liaText = ((cfg.liability.text || "").trim() || DEFAULT_LIABILITY);
  const liaPreview = cfg.liability.mode === "hard" ? liaText + "\n\nReply AGREE to continue." : liaText;
  const sfLabel = (cfg.statutory_fallback.label || "").trim() || "the applicable state property code";
  const sfPreview = `That isn't covered in the ${V.lc("org")}'s governing documents. For that, please refer to ${sfLabel}${cfg.statutory_fallback.url ? ` (${cfg.statutory_fallback.url})` : ""} and check with your manager to confirm.`;
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/config`, { liability: cfg.liability, statutory_fallback: cfg.statutory_fallback });
      toast.success("Legal & compliance settings saved");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <section data-testid="legal-compliance" className={`bg-white rounded-3xl border overflow-hidden ${readOnly ? "border-sand-300" : "border-sand-200"}`} data-locked={readOnly || undefined}>
      <button data-testid="legal-compliance-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 p-5 md:px-6 text-left transition-colors ${readOnly ? "bg-sand-50/80 hover:bg-sand-100/70" : "hover:bg-sand-50"}`}>
        <LockedTile locked={readOnly}><span className={`h-9 w-9 rounded-xl flex items-center justify-center ${readOnly ? "bg-sand-100" : "bg-redline-50"}`}><Scale className={`h-5 w-5 ${readOnly ? "text-ink/60" : "text-redline-500"}`} strokeWidth={1.5} /></span></LockedTile>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold flex items-center gap-2 flex-wrap">Legal &amp; Compliance
            {readOnly && <ViewOnlyPill testid="legal-compliance-view-only" reason={lockReason || "redline"} />}
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${cfg.liability.enabled ? "bg-emerald-100 text-emerald-700" : "bg-sand-100 text-ink/40"}`}>Disclaimer {cfg.liability.enabled ? "on" : "off"}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${cfg.statutory_fallback.enabled ? "bg-emerald-100 text-emerald-700" : "bg-sand-100 text-ink/40"}`}>Statutory fallback {cfg.statutory_fallback.enabled ? "on" : "off"}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">Safety guardrails for how the AI answers {V.lcs("person")} — add a disclaimer and stop the bot guessing on rules your docs don't cover.</div>
        </div>
        <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`px-5 md:px-6 pb-6 pt-5 space-y-5 border-t border-sand-100 ${readOnly ? "rl-view-only" : ""}`} {...(readOnly ? lockedProps(lockReason || "redline") : {})}>
          <p className="text-sm text-muted-foreground">Two optional guardrails. The first adds a disclaimer so the {V.lc("org")} isn't on the hook for the bot's answers; the second stops the AI from guessing on rules your documents don't cover and points {V.lcs("person")} to the law instead. Each shows a live preview of exactly what the {V.lc("person")} receives.</p>
      <div className="rounded-2xl border border-sand-200 p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> AI-reliance liability acknowledgment</div>
            <p className="text-xs text-muted-foreground mt-0.5">{V.many("person")} acknowledge that answers are general guidance, not legal advice.</p>
          </div>
          <Switch data-testid="liability-enabled" disabled={readOnly} checked={!!cfg.liability.enabled} onCheckedChange={(v) => upL("enabled", v)} />
        </div>
        {cfg.liability.enabled && (
          <>
            <div className="text-xs font-semibold text-muted-foreground">How it's shown</div>
            <div className="flex flex-wrap gap-2">
              {[["soft", "Soft — shown once, continuing = acceptance"], ["hard", "Hard — must reply AGREE first"]].map(([v, label]) => (
                <button key={v} disabled={readOnly} data-testid={`liability-mode-${v}`} onClick={() => upL("mode", v)}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${cfg.liability.mode === v ? "bg-ink text-white border-ink" : "bg-white border-sand-200 hover:border-ink"}`}>{label}</button>
              ))}
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Disclaimer wording (leave blank for the recommended default)</label>
              <textarea data-testid="liability-text" disabled={readOnly} rows={3} value={cfg.liability.text || ""} onChange={(e) => upL("text", e.target.value)}
                placeholder={DEFAULT_LIABILITY}
                className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            </div>
            <div data-testid="liability-preview" className="rounded-xl bg-sand-50 border border-sand-200 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-ink-500 mb-1.5">What the {V.lc("person")} sees</div>
              <div className="bg-white border border-sand-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-ink whitespace-pre-line max-w-md">{liaPreview}</div>
              <p className="text-[11px] text-muted-foreground mt-1.5">{cfg.liability.mode === "hard" ? `${V.many("person")} can't get answers until they reply AGREE (recorded with a timestamp).` : "Shown once at the start of a conversation; continuing counts as acceptance."}</p>
            </div>
          </>
        )}
      </div>
      <div className="rounded-2xl border border-sand-200 p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Landmark className="h-4 w-4 text-sky-600" /> Statutory fallback</div>
            <p className="text-xs text-muted-foreground mt-0.5">When your governing docs don't answer a rule, the concierge refers {V.lcs("person")} to the state property code instead of guessing.</p>
          </div>
          <Switch data-testid="statutory-enabled" disabled={readOnly} checked={!!cfg.statutory_fallback.enabled} onCheckedChange={(v) => upS("enabled", v)} />
        </div>
        {cfg.statutory_fallback.enabled && (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Code label</label>
                <input data-testid="statutory-label" disabled={readOnly} value={cfg.statutory_fallback.label || ""} onChange={(e) => upS("label", e.target.value)}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" placeholder="Texas Property Code" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Link (URL)</label>
                <input data-testid="statutory-url" disabled={readOnly} value={cfg.statutory_fallback.url || ""} onChange={(e) => upS("url", e.target.value)}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" placeholder="https://statutes.capitol.texas.gov/..." />
              </div>
            </div>
            <div data-testid="statutory-preview" className="rounded-xl bg-sand-50 border border-sand-200 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-ink-500 mb-1.5">What the {V.lc("person")} sees (when a rule isn't in your docs)</div>
              <div className="bg-white border border-sand-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-ink max-w-md">{sfPreview}</div>
            </div>
          </>
        )}
      </div>
      {!readOnly && (
        <button data-testid="legal-save-btn" onClick={save} disabled={saving} className="rounded-full bg-ink text-white font-semibold px-6 py-2.5 hover:bg-ink-700 transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save legal & compliance"}
        </button>
      )}
        </div>
      )}
    </section>
  );
}
